/**
 *  Sagas for the ResourceNames resource managment using Swagger client
 *
 * Each saga watcher intercepts a trigger action, does the asyncrhonous work in the respective worker saga and dispatches a success or a failure action.
 * Fetch calls are made via the swagger tags interface requests
 *
 */

import {
  apiClient,
  applyHeaders,
  buildHeaders,
  generateNonce,
} from "@bkry/bowline-utils";
import { ethers } from "ethers";
import { call, put, takeEvery } from "redux-saga/effects";
import { storeIncluded } from "../dictionarySagas";
import { actions } from "./index";

/** List Saga
 *  @description: connects to the getCollaborator operation
 */
export function* list(action) {
  let headers = yield buildHeaders();
  let { projectId, pageNum, itemNum, sorting, filters } = action.payload;
  try {
    const SwaggerClient = yield call(apiClient);
    const payload = yield call(
      SwaggerClient.apis.ProjectCreators.listProjectCreators,
      {
        project_id: projectId,
        page: pageNum,
        items: itemNum,
        sorting: JSON.stringify(sorting),
        filters: JSON.stringify(filters),
      },
      { requestInterceptor: applyHeaders(headers) }
    );
    if (payload.obj.included)
      yield* storeIncluded({ payload: payload.obj.included });
    yield put(actions.listSuccess(payload));
  } catch (e) {
    yield put(actions.listFail({ message: e.message, ...e }));
    if (e.status === 403) {
      yield* handleUnauthorized({ payload: e });
    }
  }
}

/** Show Saga
 *  @description: connects to the showCollaborator operation
 *  @param {number} action.payload the Collaborator id
 */
export function* show(action) {
  let headers = yield buildHeaders();
  try {
    const SwaggerClient = yield call(apiClient);
    const payload = yield call(
      SwaggerClient.apis.ProjectCreators.showProjectCreators,
      action.payload,
      {
        requestInterceptor: applyHeaders(headers),
      }
    );
    // we skip here the includes because we dont want to override our current project
    yield put(actions.showSuccess(payload.obj.data));
  } catch (e) {
    yield put(actions.showFail({ message: e.message, ...e }));
  }
}

export function* update(action) {
  const { project_id, id, ...requestBody } = action.payload;
  let headers = yield buildHeaders();
  try {
    const SwaggerClient = yield call(apiClient);
    let payload = yield call(
      SwaggerClient.apis.ProjectCreators.updateProjectCreators,
      { project_id: project_id, id: id },
      {
        requestInterceptor: applyHeaders(headers),
        requestBody,
      }
    );

    yield put(actions.updateSuccess(payload.obj.data));
  } catch (e) {
    yield put(actions.updateFail({ message: e.message, ...e }));
  }
}

export function* create(action) {
  let headers = yield buildHeaders();
  let { project_id, ...requestBody } = action.payload;
  try {
    const SwaggerClient = yield call(apiClient);
    let payload = yield call(
      SwaggerClient.apis.ProjectCreators.createProjectCreators,
      { project_id: project_id },
      {
        requestInterceptor: applyHeaders(headers),
        requestBody,
      }
    );

    if (payload.obj?.included)
      yield* storeIncluded({ payload: payload.obj.included });
    yield put(actions.createSuccess(payload.obj.data));
  } catch (e) {
    yield put(actions.createFail({ message: e.message, ...e }));
  }
}

export function* remove(action) {
  const { project_id, id } = action.payload;
  let headers = yield buildHeaders();
  try {
    const SwaggerClient = yield call(apiClient);
    let payload = yield call(
      SwaggerClient.apis.ProjectCreators.deleteProjectCreatorForProject,
      { project_id: project_id, id },
      {
        requestInterceptor: applyHeaders(headers),
      }
    );

    yield put(actions.removeSuccess(payload.ok));
  } catch (e) {
    yield put(actions.removeFail({ message: e.message, ...e }));
  }
}

export function* removeReset(action) {
  if (action.payload?.projectId) {
    yield put(push(`/projects/${action.payload.projectId}/collaborators`));
  }
}

/**
 * Saga Watchers
 * The exported list of sagas registered. When one of the action types is dispatched
 * the related worker saga is invoked.
 * Each saga is executed in a different thread
 */
function* collaboratorsSaga() {
  yield takeEvery(actions.list, list);
  yield takeEvery(actions.update, update);
  yield takeEvery(actions.create, create);
  yield takeEvery(actions.show, show);
  yield takeEvery(actions.remove, remove);
  yield takeEvery(actions.removeReset, removeReset);
}
export default collaboratorsSaga;
